import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Flex,
  Heading,
  TextField,
  Divider,
  Text,
  DropZone,
  VisuallyHidden,
  Label,
  View,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import { fetchAuthSession } from "aws-amplify/auth";
import { uploadData, remove } from "aws-amplify/storage";
import { StorageImage } from "@aws-amplify/ui-react-storage";
import { useProfileInsuranceQuery } from "./api/useProfileInsuranceQuery";
import {
  PostInsuranceMutation,
  PutInsuranceMutation,
} from "./api/useProfileInsuranceMutation";
import { useUserQuery } from "../common/useUserQuery";
import { DatePicker } from "rsuite";
import { formatDate } from "../common/useDateFunctions";
import { resolver } from "./validations/insuranceValidations";
import { useProfileGeneralQuery } from "./api/useProfileGeneralQuery";
import {
  PostGeneralMutation,
  PutGeneralMutation,
} from "./api/useProfileGeneralMutation";

const acceptedFileTypes = ["image/png", "image/jpeg"];

const defaultValues = {
  providerName: "",
  planName: "",
  effectiveDate: "",
  effectiveDateEnd: "",
  policyNumber: "",
  groupNumber: "",
  subscriberEmployer: "",
  subscriberAddress1: "",
  subscriberAddress2: "",
  subscriberCity: "",
  subscriberState: "",
  subscriberZipCode: "",
  urlFrontCard: {},
  urlBackCard: {},
  generalResult: {
    allowVoiceMessage: 0,
    allowMailMessage: 0,
    allowEmailMessage: 0,
    allowSmsMessage: 0,
    allowHealthInfoExchange: 0,
    acceptHIPAANotice: 0,
    religion: "",
    race: "",
    language: "",
    employerName: "",
    employerAddr1: "",
    employerAddr2: "",
    employerCity: "",
    employerState: "",
    employerZip: "",
  },
};

const removeInsuranceImage = async (path) => {
  try {
    await remove({
      key: path,
    });
  } catch (error) {
    console.log("Error ", error);
  }
  return null;
};

const uploadInsuranceImage = async (file, fileName) => {
  try {
    const session = await fetchAuthSession();
    const fileExtension = file.name.split(".").pop();
    const customPath = `users/${session.tokens.idToken.payload[
      "cognito:username"
    ].toString()}/Insurance/${fileName}.${fileExtension}`;
    const result = await uploadData({
      key: customPath,
      data: file,
      options: {
        onProgress: ({ transferredBytes, totalBytes }) => {
          if (totalBytes) {
            console.log(
              `Upload progress ${Math.round(
                (transferredBytes / totalBytes) * 100
              )} %`
            );
          }
        },
      },
    }).result;
    return result.key;
  } catch (error) {
    console.log(error);
  }
  return null;
};

export default function InsurancePage() {
  const { data: userQueryData } = useUserQuery();
  const navigate = useNavigate();
  const hiddenFrontInput = React.useRef(null);
  const hiddenBackInput = React.useRef(null);
  const {
    isPending,
    error,
    data: profileInsurance,
    isFetching,
    isSuccess,
  } = useProfileInsuranceQuery(userQueryData?.patientUserId);
  const { data: generalResult } = useProfileGeneralQuery(
    userQueryData?.patientUserId
  );

  const postInsurance = PostInsuranceMutation(userQueryData?.patientUserId);
  const putInsurance = PutInsuranceMutation(userQueryData?.patientUserId);
  const { mutate: putGeneral } = PutGeneralMutation();
  const { mutate: postGeneral } = PostGeneralMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues,
    values: {
      ...profileInsurance,
      generalResult: generalResult ?? {},
    },
    resolver: resolver,
  });

  useEffect(() => {
    if (profileInsurance != null && generalResult != null) {
      reset({ ...profileInsurance, generalResult: generalResult ?? {} });
    }
  }, [profileInsurance, generalResult]);

  if (isPending) return "Loading...";

  if (error) return "An error has occurred: " + error.message;

  if (isSuccess)
    return (
      <>
        <div>{isFetching ? "Updating..." : ""}</div>
        <form
          onSubmit={handleSubmit(async (values) => {
            const formatEffectiveDate = new Date(values.effectiveDate);
            const formatEndDate = new Date(values.effectiveDateEnd);

            try {
              let frontUrl = values.urlFrontCard;
              let backUrl = values.urlBackCard;
              if (typeof frontUrl?.name === "string") {
                if (
                  profileInsurance?.urlFrontCard &&
                  profileInsurance?.urlFrontCard.indexOf("users") > -1
                ) {
                  await removeInsuranceImage(profileInsurance?.urlFrontCard);
                }
                frontUrl = await uploadInsuranceImage(
                  values.urlFrontCard,
                  "frontInsuranceCard"
                );
              }
              if (typeof backUrl?.name === "string") {
                if (
                  profileInsurance?.urlBackCard &&
                  profileInsurance?.urlBackCard.indexOf("users") > -1
                ) {
                  await removeInsuranceImage(profileInsurance?.urlFrontCard);
                }
                backUrl = await uploadInsuranceImage(
                  values.urlBackCard,
                  "backInsuranceCard"
                );
              }
              values.patientUserId = userQueryData?.patientUserId;
              if (profileInsurance && profileInsurance.patientUserId) {
                const dirtyPayload = JSON.parse(
                  JSON.stringify({
                    ...values,
                    effectiveDate: formatDate(formatEffectiveDate),
                    effectiveDateEnd: formatDate(formatEndDate),
                    urlFrontCard: frontUrl,
                    urlBackCard: backUrl,
                  })
                );
                putInsurance.mutateAsync(dirtyPayload);
              } else {
                postInsurance.mutateAsync({
                  ...values,
                  effectiveDate: formatDate(formatEffectiveDate),
                  effectiveDateEnd: formatDate(formatEndDate),
                  urlFrontCard: frontUrl,
                  urlBackCard: backUrl,
                  patientUserId: userQueryData?.patientUserId,
                });
              }
              if (generalResult && generalResult.patientUserId) {
                putGeneral({
                  patientUserId: userQueryData?.patientUserId,
                  ...values.generalResult,
                });
              } else {
                postGeneral({
                  patientUserId: userQueryData?.patientUserId,
                  ...values.generalResult,
                });
              }
              reset();
              navigate("/user/quick-actions/profile-summary");
            } catch (error) {
              console.log("Error : ", error);
            }
          })}
        >
          <FlexFormContainer headerTitle={"Insurance"}>
            <Heading level="6" children="Primary"></Heading>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="providerName"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Provider name"
                    {...field}
                    value={field.value ?? profileInsurance?.providerName}
                    errorMessage={errors.providerName?.message}
                    hasError={!!errors.providerName}
                  />
                )}
              />
              <Controller
                name="planName"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Plan name"
                    {...field}
                    value={field.value ?? profileInsurance?.planName}
                    errorMessage={errors.planName?.message}
                    hasError={!!errors.planName}
                  />
                )}
              />
            </Flex>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Flex
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Label htmlFor="effectiveDate">Effective Date</Label>
                <Controller
                  name="effectiveDate"
                  control={control}
                  defaultValue={profileInsurance?.effectiveDate}
                  render={({ field }) => (
                    <>
                      <DatePicker
                        id={field.name}
                        onChange={(value) => field.onChange(value)}
                        value={
                          field.value != null ? new Date(field.value) : null
                        }
                        format="yyyy-MM-dd"
                        oneTap
                        style={{ width: "300px" }}
                        size="lg"
                      />
                      {!!errors.effectiveDate && (
                        <Text
                          variation="error"
                          as="p"
                          lineHeight="0.5em"
                          fontWeight={400}
                          fontSize="0.8em"
                          fontStyle="normal"
                          textDecoration="none"
                          width="30vw"
                          style={{ marginLeft: "10px" }}
                        >
                          {errors.effectiveDate.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Flex>
              <Flex
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Label htmlFor="endDate">End Date</Label>
                <Controller
                  name="effectiveDateEnd"
                  control={control}
                  defaultValue={profileInsurance?.effectiveDateEnd}
                  render={({ field }) => (
                    <>
                      <DatePicker
                        id={field.name}
                        onChange={(value) => field.onChange(value)}
                        value={
                          field.value != null ? new Date(field.value) : null
                        }
                        format="yyyy-MM-dd"
                        oneTap
                        style={{ width: "300px" }}
                        size="lg"
                      />
                      {!!errors.effectiveDateEnd && (
                        <Text
                          variation="error"
                          as="p"
                          lineHeight="0.5em"
                          fontWeight={400}
                          fontSize="0.8em"
                          fontStyle="normal"
                          textDecoration="none"
                          width="30vw"
                          style={{ marginLeft: "10px" }}
                        >
                          {errors.effectiveDateEnd.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Flex>
            </Flex>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="policyNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Policy number"
                    {...field}
                    value={field.value ?? profileInsurance?.policyNumber}
                    errorMessage={errors.policyNumber?.message}
                    hasError={!!errors.policyNumber}
                  />
                )}
              />
              <Controller
                name="groupNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Group number"
                    {...field}
                    value={field.value ?? profileInsurance?.groupNumber}
                    errorMessage={errors.groupNumber?.message}
                    hasError={!!errors.groupNumber}
                  />
                )}
              />
            </Flex>
            <Divider size="small" />
            <Controller
              name="subscriberEmployer"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="160px"
                  label="Subscriber employer"
                  {...field}
                  value={field.value ?? profileInsurance?.subscriberEmployer}
                  errorMessage={errors.subscriberEmployer?.message}
                  hasError={!!errors.subscriberEmployer}
                />
              )}
            />
            <Text
              fontSize="14px"
              fontWeight="400"
              children="*If uninsured enter Student or leave blank"
            />
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="subscriberAddress1"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Subscriber address 1"
                    {...field}
                    value={field.value ?? profileInsurance?.subscriberAddress1}
                    errorMessage={errors.subscriberAddress1?.message}
                    hasError={!!errors.subscriberAddress1}
                  />
                )}
              />
              <Controller
                name="subscriberAddress2"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Subscriber address 2"
                    {...field}
                    value={field.value ?? profileInsurance?.subscriberAddress2}
                    errorMessage={errors.subscriberAddress2?.message}
                    hasError={!!errors.subscriberAddress2}
                  />
                )}
              />
            </Flex>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="subscriberCity"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Subscriber city"
                    {...field}
                    value={field.value ?? profileInsurance?.subscriberCity}
                    errorMessage={errors.subscriberCity?.message}
                    hasError={!!errors.subscriberCity}
                  />
                )}
              />
              <Controller
                name="subscriberState"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Subsscriber state"
                    {...field}
                    value={field?.value ?? profileInsurance?.subscriberState}
                    errorMessage={errors.subscriberState?.message}
                    hasError={!!errors.subscriberState}
                  />
                )}
              />
            </Flex>
            <Controller
              name="subscriberZipCode"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Subscriber zip code"
                  {...field}
                  value={field?.value ?? profileInsurance?.subscriberZipCode}
                  errorMessage={errors.subscriberZipCode?.message}
                  hasError={!!errors.subscriberZipCode}
                />
              )}
            />
            <Divider size="small" />
            <Heading level={6}>Employer Information</Heading>
            <Controller
              name="generalResult.employerName"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Employer Name"
                  {...field}
                  value={field.value ?? generalResult?.employerName}
                />
              )}
            />
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="generalResult.employerAddr1"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Employer Address 1"
                    {...field}
                    value={field.value ?? generalResult?.employerAddr1}
                  />
                )}
              />
              <Controller
                name="generalResult.employerAddr2"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Employer Address 2"
                    {...field}
                    value={field.value ?? generalResult?.employerAddr2}
                  />
                )}
              />
            </Flex>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="generalResult.employerCity"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Employer City"
                    {...field}
                    value={field.value ?? generalResult?.employerCity}
                  />
                )}
              />
              <Controller
                name="generalResult.employerState"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Employer State"
                    {...field}
                    value={field.value ?? generalResult?.employerState}
                  />
                )}
              />
            </Flex>
            <Controller
              name="generalResult.employerZip"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Employer Zip"
                  {...field}
                  value={field.value ?? generalResult?.employerZip}
                />
              )}
            />
            <Divider size="small" />
            <Heading level="6" children="Proof of Insurance"></Heading>

            <Flex
              direction={"row"}
              justifyContent={"center"}
              alignContent={"center"}
              alignItems={"center"}
            >
              {profileInsurance?.urlFrontCard && (
                <Flex>
                  <StorageImage
                    path={`public/${profileInsurance.urlFrontCard}`}
                    alt="front of insurance card"
                    maxHeight={200}
                    validateObjectExistence={true}
                  />
                </Flex>
              )}

              <Flex direction={"row"} justifyContent={"center"}>
                <Controller
                  name="urlFrontCard"
                  control={control}
                  render={({
                    field: { onChange, value: frontValue, name, control },
                  }) => (
                    <>
                      <DropZone
                        name={name}
                        control={control}
                        acceptedFileTypes={acceptedFileTypes}
                        onDropComplete={({ acceptedFiles }) => {
                          onChange(acceptedFiles[0]);
                        }}
                        borderWidth="2px"
                        borderColor="#077A92"
                      >
                        <Flex direction={"column"}>
                          <Text>Drag front image here or</Text>
                          <Button
                            size="small"
                            onClick={() => hiddenFrontInput.current.click()}
                          >
                            Browse
                          </Button>
                        </Flex>
                        <VisuallyHidden>
                          <input
                            type="file"
                            tabIndex={-1}
                            ref={hiddenFrontInput}
                            onChange={(e) => {
                              const files = e.target.files;
                              setValue("urlFrontCard", files[0]);
                            }}
                            multiple={false}
                            accept={acceptedFileTypes.join(",")}
                          />
                        </VisuallyHidden>
                        {frontValue && <Text>{frontValue.name}</Text>}
                      </DropZone>
                      {!!errors.urlFrontCard && (
                        <Text
                          variation="error"
                          as="p"
                          lineHeight="0.5em"
                          fontWeight={400}
                          fontSize="0.8em"
                          fontStyle="normal"
                          textDecoration="none"
                          width="30vw"
                          style={{ marginLeft: "30px" }}
                        >
                          {errors.urlFrontCard.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Flex>
            </Flex>

            <Flex
              direction={"row"}
              justifyContent={"center"}
              alignContent={"center"}
              alignItems={"center"}
            >
              {profileInsurance?.urlBackCard && (
                <Flex>
                  <StorageImage
                    path={`public/${profileInsurance.urlBackCard}`}
                    alt="front of insurance card"
                    maxHeight={200}
                    validateObjectExistence={true}
                  />
                </Flex>
              )}
              <Flex direction={"row"} justifyContent={"center"}>
                <Controller
                  name="urlBackCard"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <>
                      <DropZone
                        name={name}
                        acceptedFileTypes={acceptedFileTypes}
                        onDropComplete={({ acceptedFiles, rejectedFiles }) => {
                          onChange(acceptedFiles[0]);
                        }}
                        borderWidth="2px"
                        borderColor="#077A92"
                      >
                        <Flex direction={"column"}>
                          <Text>Drag back images here or</Text>
                          <Button
                            size="small"
                            onClick={() => hiddenBackInput.current.click()}
                          >
                            Browse
                          </Button>
                        </Flex>
                        <VisuallyHidden>
                          <input
                            type="file"
                            tabIndex={-1}
                            ref={hiddenBackInput}
                            onChange={(e) => {
                              const files = e.target.files;
                              setValue("urlBackCard", files[0]);
                            }}
                            multiple={false}
                            accept={acceptedFileTypes.join(",")}
                          />
                        </VisuallyHidden>
                        {value && <Text>{value.name}</Text>}
                      </DropZone>
                      {!!errors.urlBackCard && (
                        <Text
                          variation="error"
                          as="p"
                          lineHeight="0.5em"
                          fontWeight={400}
                          fontSize="0.8em"
                          fontStyle="normal"
                          textDecoration="none"
                          width="30vw"
                          style={{ marginLeft: "30px" }}
                        >
                          {errors.urlBackCard.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Flex>
            </Flex>
            <Text
              fontSize="14px"
              fontWeight="400"
              children="*Upload Front and Back of Insurance Card"
            />
            <Flex gap="18px" direction="column">
              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
}
